/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { Card, CardContent } from '@repo/shared-client/components/ui'
import { useTranslation } from '@web/src/lib/intl/react'
import type { AdminPaymentsSummaryQuery, AdminPaymentsSummaryQueryVariables } from 'types/graphql'

export const ADMIN_PAYMENTS_SUMMARY_QUERY: TypedDocumentNode<
  AdminPaymentsSummaryQuery,
  AdminPaymentsSummaryQueryVariables
> = gql`
  query AdminPaymentsSummaryQuery {
    adminPaymentsSummary {
      pendingCount
      pendingTotal
      approvedThisMonthTotal
      refundCount
      refundRate
    }
  }
`

export function formatPaymentUsd(amount: number): string {
  return `$${amount.toFixed(2)}`
}

export function formatPaymentRefundRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`
}

type AdminPaymentsSummaryStripProps = {
  summary: AdminPaymentsSummaryQuery['adminPaymentsSummary'] | null | undefined
}

export function AdminPaymentsSummaryStrip({ summary }: AdminPaymentsSummaryStripProps) {
  const { t } = useTranslation()

  const items = [
    { label: t('Pending'), value: summary ? String(summary.pendingCount) : '—' },
    { label: t('Pending total'), value: summary ? formatPaymentUsd(summary.pendingTotal) : '—' },
    { label: t('Approved this month'), value: summary ? formatPaymentUsd(summary.approvedThisMonthTotal) : '—' },
    { label: t('Refunds'), value: summary ? String(summary.refundCount) : '—' },
    { label: t('Refund rate'), value: summary ? formatPaymentRefundRate(summary.refundRate) : '—' },
  ]

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
      {items.map((item) => (
        <Card className="px-0 py-3" key={item.label}>
          <CardContent className="flex flex-col gap-1">
            <p className="text-muted-foreground text-sm">{item.label}</p>
            <p className="font-medium text-2xl tabular-nums">{item.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
